import React, { useRef } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { resolveImageDisplayUrl } from '../../config/Config';

/**
 * Reusable optional image field used for main question, sub-question, and detail answer.
 * Layout: dashed box with paste support; when image present: preview + Remove bottom-right;
 * when empty: centered instruction; below box: Attach Image File button.
 */
const OptionalImageField = ({
  label = 'Image (optional)',
  previewUrl = '',
  onClear,
  onFileSelect,
  onPaste,
  disabled = false,
  minHeight = 160,
  emptyInstruction = 'Click to focus, then paste screenshot (Ctrl+V) here to attach image',
  attachButtonLabel = 'Attach Image File',
  inputId = 'optional-image-file',
  imageAlt = 'preview',
  maxImageHeight = 180,
}) => {
  const fileInputRef = useRef(null);

  const hasPreview = Boolean(previewUrl && String(previewUrl).trim());
  const displaySrc = hasPreview
    ? (previewUrl.startsWith('blob:') || previewUrl.startsWith('data:')
        ? previewUrl
        : resolveImageDisplayUrl(previewUrl))
    : '';

  const handleBoxClick = (e) => {
    if (e.target.closest('button')) return;
    e.currentTarget.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      fileInputRef.current?.click();
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>
      <Box
        sx={{
          border: '2px dashed grey',
          borderRadius: 2,
          p: 2,
          textAlign: 'center',
          opacity: disabled ? 0.4 : 1,
          minHeight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          position: 'relative',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'action.hover',
          },
        }}
        onPaste={(e) => {
          if (disabled) return;
          const items = e.clipboardData?.items;
          if (items) {
            for (let i = 0; i < items.length; i++) {
              const item = items[i];
              if (item.type.startsWith('image/')) {
                const file = item.getAsFile();
                if (file && onPaste) onPaste(file);
                break;
              }
            }
          }
        }}
        onClick={handleBoxClick}
        tabIndex={0}
        onKeyDown={handleKeyDown}
      >
        {hasPreview ? (
          <Box sx={{ position: 'relative', width: '100%', display: 'inline-block' }}>
            <img
              src={displaySrc}
              alt={imageAlt}
              style={{ maxWidth: '100%', maxHeight: maxImageHeight, display: 'block', objectFit: 'contain' }}
            />
            <Button
              type="button"
              variant="outlined"
              color="error"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (onClear) onClear();
              }}
              sx={{ position: 'absolute', bottom: 8, right: 8, zIndex: 1 }}
            >
              Remove Image
            </Button>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {emptyInstruction}
          </Typography>
        )}
      </Box>
      <Button
        variant="outlined"
        component="label"
        htmlFor={inputId}
        disabled={disabled}
        sx={{ mt: 1 }}
      >
        {attachButtonLabel}
        <input
          id={inputId}
          ref={fileInputRef}
          type="file"
          hidden
          accept="image/*"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file && onFileSelect) onFileSelect(file);
            e.target.value = '';
          }}
        />
      </Button>
    </Box>
  );
};

export default OptionalImageField;
